import { Link } from "react-router-dom";

/* ---------------------------------------------------------------------------
   Content on this page is taken verbatim from Daksfirst_Product_Guide.pdf
   (3 pages, issued 10 October 2026). Do not edit figures here — update the
   guide first, then mirror the change, so the page and the PDF never diverge.
--------------------------------------------------------------------------- */

const AS_AT = "10 October 2026";
const GUIDE = "/docs/Daksfirst_Product_Guide.pdf";

const GLANCE = [
  {
    name: "Residential Bridging",
    size: "£1m to £10m",
    ltv: "Up to 75%",
    term: "6 to 18 months",
    rate: "0.79–1.05%",
    rateSub: "per month"
  },
  {
    name: "Commercial Bridging",
    size: "£1m to £10m",
    ltv: "Up to 70%",
    term: "6 to 18 months",
    rate: "0.89–1.10%",
    rateSub: "per month"
  },
  {
    name: "Buy-to-Let Term Loan",
    size: "£1m to £10m",
    ltv: "Up to 70%",
    term: "2 to 5 years",
    rate: "SONIA + 2.75–3.75%",
    rateSub: "pa · fixed and floating"
  },
  {
    name: "Commercial Term Loan",
    size: "£1m to £10m",
    ltv: "Up to 65%",
    term: "2 to 5 years",
    rate: "SONIA + 3.50–5.50%",
    rateSub: "pa · fixed and floating"
  }
];

const BORROWERS = [
  "UK individuals (residential products)",
  "UK limited companies and PLCs",
  "LLPs and partnerships",
  "SPVs, with guarantees from directors and shareholders",
  "Trusts, onshore and offshore",
  "Opco / propco structures",
  "Offshore companies in recognised jurisdictions",
  "Pension schemes (SIPP / SSAS) on commercial property, max 50% LTV"
];

const STEPS = [
  {
    t: "Submit the case",
    d: (
      <>
        Email{" "}
        <a href="mailto:raft@uk.daksfirst.com" style={{ color: "var(--gold)" }}>
          raft@uk.daksfirst.com
        </a>{" "}
        with the borrower, property address, loan amount, term and purpose
      </>
    )
  },
  { t: "Decision in Principle", d: "Reviewed and approved by our credit team" },
  { t: "Valuation and legals", d: "Panel valuer and solicitors instructed in parallel" },
  { t: "Credit approval and completion", d: "Final terms approved by Credit Committee" }
];

const RESI_TERMS = [
  {
    name: "Residential Bridging",
    rows: [
      ["Loan size", "£1m to £10m"],
      ["Below-market purchases", "Up to 90% of purchase price", "Within the LTV limit. No ground-up development."],
      ["Max LTV", "Up to 75%"],
      ["Term", "6 to 18 months"],
      ["Rate", "0.79% to 1.05% per month"],
      ["Interest", "Rolled, serviced or retained"],
      ["Fees", "1% product fee · 1% exit fee", "No exit fee when the bridge rolls into a Daksfirst term loan."],
      ["Location", "England & Wales", "Mainland Scotland by exception"]
    ]
  },
  {
    name: "Buy-to-Let Term Loan",
    rows: [
      ["Loan size", "£1m to £10m"],
      ["Below-market purchases", "Up to 80% of purchase price", "Within the LTV limit. No ground-up development."],
      ["Max LTV", "Up to 70%"],
      ["Term", "2 to 5 years"],
      ["Rate", "SONIA + 2.75% to 3.75% pa", "Fixed and floating options"],
      ["Interest", "Serviced from rent", "ICR 1.25x at a stressed rate"],
      ["Fees", "1% product fee", "No exit fee. Early repayment charges apply."],
      ["Location", "England & Wales", "Mainland Scotland by exception"]
    ]
  }
];

const RESI_LOOK = [
  ["Tenancy", "Assured Shorthold Tenancy in place. For bridging, short-let and serviced use considered with clear exit evidence."],
  ["No regulated lending", "No regulated bridging or Consumer Buy-to-Let. No lending where the borrower or a related person occupies more than 40% of the property."],
  ["Rent from day one (BTL)", "Provable from the security or verified portfolio income. Cash savings are not a servicing source."],
  ["Purchases", "Loan based on the lower of price and valuation. Auction price accepted as evidence; LPA Receiver sales where there is no vendor–borrower link."],
  ["HMOs", "Accepted with licence, management arrangements and tenancy schedule."],
  ["Borrower credit", "No undischarged bankruptcy or live Debt Management Plan. Minor adverse considered by Credit Committee."]
];

const RESI_LISTS = [
  {
    h: "Property we lend on",
    items: [
      "Freehold houses of all types",
      "Flats and maisonettes, incl. long leasehold (40+ years at maturity)",
      "Licensed and managed HMOs",
      "Mixed use"
    ]
  },
  {
    h: "Bridging is used for",
    items: [
      "Purchase — auction or private treaty",
      "Refinance",
      "Sales-period loans",
      "Equity release for business purposes"
    ]
  },
  {
    h: "Buy-to-let is used for",
    items: [
      "Long-term hold of a let portfolio",
      "Portfolio consolidation and refinance",
      "Exit from a Daksfirst or third-party bridge",
      "Equity release from let property"
    ]
  }
];

const COMM_TERMS = [
  {
    name: "Commercial Bridging",
    rows: [
      ["Loan size", "£1m to £10m"],
      ["Below-market purchases", "Up to 80% of purchase price", "Within the LTV limit. No ground-up development."],
      ["Max LTV", "Up to 70%"],
      ["Term", "6 to 18 months"],
      ["Rate", "0.89% to 1.10% per month"],
      ["Interest", "Rolled, serviced or retained"],
      ["Fees", "1% product fee · 1% exit fee", "No exit fee when the bridge rolls into a Daksfirst term loan."],
      ["Location", "England & Wales", "Mainland Scotland by exception"]
    ]
  },
  {
    name: "Commercial Term Loan",
    rows: [
      ["Loan size", "£1m to £10m"],
      ["Below-market purchases", "Up to 75% of purchase price", "Within the LTV limit. No ground-up development."],
      ["Max LTV", "Up to 65%"],
      ["Term", "2 to 5 years"],
      ["Rate", "SONIA + 3.50% to 5.50% pa", "Fixed and floating options"],
      ["Interest", "Serviced from income", "ICR 1.35x at a stressed rate"],
      ["Fees", "1% product fee", "No exit fee. Early repayment charges apply."],
      ["Location", "England & Wales", "Mainland Scotland by exception"]
    ]
  }
];

const COMM_LOOK = [
  ["Let property", "Tenancy schedule, lease length, break dates and tenant covenant reviewed. Vacant space valued on vacant possession."],
  ["Trading assets", "Operator or tenant accounts are required. Where value depends on a licence, we lend on vacant possession value."],
  ["PBSA and serviced apartments", "Occupancy, letting history and operator covenant reviewed. Opco/propco structures considered."],
  ["Income from day one (term loan)", "Rent or trading income must be provable from the security on day one. Vacant or awaiting a tenant? Start on Commercial Bridging and move to the term loan once let."],
  ["Insurance", "Buildings and, for trading assets, business-interruption cover with Daksfirst noted."],
  ["Exit (bridging)", "Sale: valuer evidence of comparable sales. Refinance: accepted where income and covenant are evidenced."]
];

const COMM_LISTS = [
  {
    h: "Property we lend on",
    items: [
      "Offices",
      "Retail and high-street units",
      "Industrial and warehouse",
      "Mixed-use buildings",
      "Hotels — trading or vacant",
      "Purpose-built student accommodation",
      "Serviced apartment schemes"
    ]
  },
  {
    h: "Bridging is used for",
    items: [
      "Acquisition, including auction",
      "Refinance of existing debt",
      "Equity release for business purposes",
      "Repositioning and refurbishment of existing buildings"
    ]
  },
  {
    h: "Term loan is used for",
    items: [
      "Long-term hold of let commercial property",
      "Refinance of existing debt",
      "Exit from a bridge",
      "Equity release for business purposes"
    ]
  }
];

function Terms({ product }) {
  return (
    <div className="pg-terms">
      <h3>{product.name}</h3>
      {product.rows.map(([k, v, sub]) => (
        <div className="pg-row" key={k}>
          <span className="k">{k}</span>
          <span className="v">{v}</span>
          {sub && <span className="sub2">{sub}</span>}
        </div>
      ))}
    </div>
  );
}

function Look({ items }) {
  return (
    <div className="pg-look">
      {items.map(([t, d]) => (
        <div key={t}>
          <span className="t">{t}</span>
          <p>{d}</p>
        </div>
      ))}
    </div>
  );
}

function Lists({ groups }) {
  return (
    <div className="pg-lists">
      {groups.map((g) => (
        <div key={g.h}>
          <h3>{g.h}</h3>
          <ul>
            {g.items.map((it) => (
              <li key={it}>{it}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function Products() {
  return (
    <>
      <section className="page-header">
        <div className="wrap">
          <div className="eyebrow">Lending products</div>
          <h1>Property finance from Daksfirst.</h1>
          <p className="sub">
            Unregulated first-charge property lending in England and Wales, and mainland
            Scotland by exception. Bridging and term loans on residential and commercial
            property for experienced investors.
          </p>
          <span className="pg-asof">Terms indicative, as at {AS_AT}</span>
        </div>
      </section>

      {/* ---------- AT A GLANCE ---------- */}
      <section>
        <div className="wrap">
          <div className="eyebrow">Our products at a glance</div>
          <div className="pg-grid">
            {GLANCE.map((p) => (
              <article className="pg-card" key={p.name}>
                <h3>{p.name}</h3>
                <dl style={{ margin: 0 }}>
                  <div className="pg-spec"><dt>Loan size</dt><dd>{p.size}</dd></div>
                  <div className="pg-spec"><dt>Max LTV</dt><dd>{p.ltv}</dd></div>
                  <div className="pg-spec"><dt>Term</dt><dd>{p.term}</dd></div>
                  <div className="pg-spec">
                    <dt>Rate</dt>
                    <dd>{p.rate}<small>{p.rateSub}</small></dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>

          <p className="pg-note">
            Below-market purchases: up to 90% of purchase price for residential bridging,
            80% for commercial bridging and BTL, 75% for commercial term loans, always
            within the LTV limit. No ground-up development. Bridging: 1% product fee and
            1% exit fee, with no exit fee when the bridge rolls into a Daksfirst term loan.
            Term loans: 1% product fee, no exit fee; early repayment charges apply.
          </p>

          <div className="pg-lead">
            <h2>Bridge to term loan</h2>
            <p>
              When any bridge expires, it can roll into a Daksfirst term loan. No exit fee
              on the bridge, lower legal and valuation costs than a fresh refinance, and a
              clear exit from day one.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- WHO WE LEND TO ---------- */}
      <section className="tight">
        <div className="wrap">
          <div className="eyebrow">Who we lend to</div>
          <div className="pg-chips">
            {BORROWERS.map((b) => (
              <span className="pg-chip" key={b}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- RESIDENTIAL ---------- */}
      <section>
        <div className="wrap">
          <div className="eyebrow">Residential lending</div>
          <h2>Residential: Bridging and Buy-to-Let</h2>
          <p className="sub" style={{ marginTop: 14 }}>
            Short-term and term finance against residential investment property. Bridge to
            buy, refinance or reposition, then hold on a Daksfirst Buy-to-Let Term Loan once
            the property is let — one lender, one credit file, no exit fee when the bridge
            rolls over.
          </p>

          <div className="pg-cols">
            {RESI_TERMS.map((p) => <Terms product={p} key={p.name} />)}
          </div>

          <div className="eyebrow" style={{ marginTop: 44 }}>What we look for</div>
          <Look items={RESI_LOOK} />

          <Lists groups={RESI_LISTS} />
        </div>
      </section>

      {/* ---------- COMMERCIAL ---------- */}
      <section>
        <div className="wrap">
          <div className="eyebrow">Commercial lending</div>
          <h2>Commercial: Bridging and Term Loans</h2>
          <p className="sub" style={{ marginTop: 14 }}>
            Short-term and term finance against commercial and operating property. Offices,
            retail, industrial, mixed-use and operating assets such as hotels, PBSA and
            serviced apartments. Bridge now, then roll into a Daksfirst Commercial Term Loan
            once income is in place.
          </p>

          <div className="pg-cols">
            {COMM_TERMS.map((p) => <Terms product={p} key={p.name} />)}
          </div>

          <div className="eyebrow" style={{ marginTop: 44 }}>What we look for</div>
          <Look items={COMM_LOOK} />

          <Lists groups={COMM_LISTS} />
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="tight">
        <div className="wrap">
          <div className="eyebrow">How it works</div>
          <div className="pg-steps">
            {STEPS.map((s, i) => (
              <div className="pg-step" key={s.t}>
                <div className="n">{i + 1}</div>
                <span className="t">{s.t}</span>
                <div className="d">{s.d}</div>
              </div>
            ))}
          </div>

          <div className="pg-dl">
            <p className="m">
              Have a case in mind? Send us the case and we will come back with indicative
              terms.
            </p>
            <a className="btn btn-primary" href="mailto:raft@uk.daksfirst.com">
              raft@uk.daksfirst.com
            </a>
            <a className="btn btn-dark" href={GUIDE} target="_blank" rel="noopener noreferrer">
              Download the product guide (PDF)
            </a>
          </div>

          <p className="pg-foot">
            For property investors and professional intermediaries only. These loans are not
            regulated by the Financial Conduct Authority. All lending is subject to status,
            valuation and credit approval.
            <br />
            Terms indicative, as at {AS_AT}. Daksfirst Limited, 8 Hill Street, Mayfair,
            London W1J 5NG. See also our{" "}
            <Link to="/tariff" style={{ color: "var(--gold)" }}>tariff of charges</Link> and{" "}
            <Link to="/policies" style={{ color: "var(--gold)" }}>borrower policies</Link>.
          </p>
        </div>
      </section>
    </>
  );
}
